import React, { useState } from "react";
import { Box, Modal, Tab, Typography, useTheme } from "@mui/material";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { MultimediaView } from "./MultimediaView";

export const officeFormats = ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "rtf"];

export const formatsAviable = {
  images: [
    "jpeg",
    "jpg",
    "png",
    "webp",
    "gif",
    "bmp",
    "tiff",
    "svg",
    "ico",
    "heic",
  ],
  documents: [
    "pdf",
    "txt",
    "csv",
    "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ...officeFormats,
  ],
  audios: [
    "mp3",
    "wav",
    "flac",
    "aac",
    "m4a",
    "wma",
    "ogg",
    "alac",
    "mpeg",
  ],
  videos: [
    "mp4",
    "mkv",
    "mov",
    "avi",
    "wmv",
    "flv",
    "webm",
  ],
}

export const ModalAttachments = ({ 
  title, 
  open = false,
  onClose,
  files,
  fileKey="url"
}) => {
  const theme = useTheme();
  const [t] = useTranslation("components");
  const [tabSelected, setTabSelected] = useState("all");

  const hdlChangeTab = (e, newValue) => {
    setTabSelected(newValue);
  };

  // Categorized files
  const categorizedFiles = {
    all: files,
    images: files.filter((file) =>
      formatsAviable.images.includes(file.file_type)
    ),
    documents: files.filter((file) =>
      formatsAviable.documents.includes(file.file_type) ||
      formatsAviable.documents.includes(file.name?.split(".").pop())
    ),
    audios: files.filter((file) =>
      formatsAviable.audios.includes(file.file_type)
    ),
    videos: files.filter((file) =>
      formatsAviable.videos.includes(file.file_type)
    ),
  };

  const tabs = [
    {
      value: "all",
      label: t("ModalAttachments.all_files"),
      data: categorizedFiles.all,
    },
    {
      value: "images",
      label: t("ModalAttachments.images"),
      data: categorizedFiles.images,
    },
    {
      value: "documents",
      label: t("ModalAttachments.documents"),
      data: categorizedFiles.documents,
    },
    {
      value: "audios",
      label: t("ModalAttachments.audios"),
      data: categorizedFiles.audios,
    },
    {
      value: "videos",
      label: t("ModalAttachments.videos"),
      data: categorizedFiles.videos,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="attachment-modal-title"
      aria-describedby="attachment-modal-description"
      sx={{ zIndex: 1054 }}
    >
      <Box
        className="rounded"
        sx={{
          backgroundColor: theme.palette.background.paper,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          border: `2px solid ${theme.palette.secondary.main}`,
          boxShadow: 24,
          minWidth: "70%",
          maxWidth: "95%",
        }}
      >
        {/* Close button */}
        <EGIconButton
          iconName="cancel_circle"
          iconColor="primary"
          onClick={onClose}
          sx={{ position: "absolute", top: 15, right: 12 }}
          iconSize={25}
        />
        {/* Title */}
        <Typography
          component="h3"
          color="secondary"
          px={2}
          pt={1.5}
          align="center"
        >
          {title || t("ModalAttachments.attachments")} 
        </Typography>

        <Box sx={{ p: 1, mt: 0 }}>
          <TabContext value={tabSelected}>
            <Box
              sx={{ borderBottom: 2, borderColor: "divider", width: "100%" }}
            >
              <TabList
                onChange={hdlChangeTab}
                aria-label="attachment tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    disabled={tab.data.length === 0}
                    className="rounded"
                    value={tab.value}
                    label={tab.label}
                    sx={{ textTransform: "none", fontSize: "0.9rem", p: 0 }}
                  />
                ))}
              </TabList>
            </Box>

            {tabs.map((tab) => (
              <TabPanel key={tab.value} value={tab.value} sx={{ padding: 0 }}>
                <MultimediaView multimedia={tab.data} fileKey={fileKey} />
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      </Box>
    </Modal>
  );
};
